import cn from 'classnames';
import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { OutsideClick } from '@webapp/common/components/outside-click';
import { useInputHotkeys } from '@webapp/common/hooks/use-input-hotkeys';
import { stopEvent } from '@webapp/common/lib/ui';

import css from './edit-name.css';

export const EditName: FC<{
    name: string;
    placeholder?: string;
    className?: string;
    onDone: () => void;
    onSave: (name: string) => void;
    isNew?: boolean;
    fullWidth?: boolean;
    style?: CSSProperties;
}> = memo(({ isNew, fullWidth, name, placeholder, onDone, onSave, style, className }) => {
    const inputRef = useRef(null);
    // null в стейте приводил к onSave(null) при быстром клике мимо поля до синка имени (FRONT-2ADE)
    const [value, setValue] = useState(name ?? '');

    const saveValue = useCallback(() => {
        if (value !== name || isNew) {
            onSave(value);
            setTimeout(onDone, 100); // TODO avoid timeout
        } else {
            onDone();
        }
    }, [value, name, isNew, onSave, onDone]);

    const onChange = useCallback(({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setValue(value);
    }, []);

    useInputHotkeys(inputRef, saveValue, onDone);

    const selectAll = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(0, inputRef.current.value.length);
        }
    }, []);

    useLayoutEffect(() => {
        selectAll();
    }, [inputRef.current]);

    useEffect(() => {
        setValue(name ?? '');
    }, [name]);

    return (
        <OutsideClick
            active={true}
            className={cn(css.wrap, className, fullWidth && css.fullWidth)}
            style={style}
            onClickOutside={saveValue}
        >
            <input
                autoFocus
                placeholder={placeholder}
                className={css.edit}
                defaultValue={value}
                ref={inputRef}
                onChange={onChange}
                onClick={stopEvent}
                onFocus={selectAll}
            />
        </OutsideClick>
    );
});

EditName.displayName = 'EditName';
